import type { Ref } from 'react';
import React, { useCallback } from 'react';
import type { ScaleProp, VoidCallback } from '../common-types';
import type { ValidIconName } from './Icon';
import type { DefaultTooltipOptions } from '../../';
import {
  noPropagationClickHandler,
  useLayerDeferedVisibility
} from '../../lib';
import { ButtonIcon } from './internal';
import { useHover } from 'react-laag';
import {
  DEFAULT_HOVER_CONFIG,
  DEFAULT_PLACEMENT_CONFIG,
  mergeRefs
} from '../../util';

/**
 * {@link ScaleProp} extended by 'default' option
 */
type ExtendedScaleProp =
  | {
      scale?: 'default';
    }
  | ScaleProp;
export type ButtonVariants =
  | 'primary'
  | 'gray'
  | 'tertiary'
  | 'ios'
  | 'primary-neutral';
/**
 * Props for a {@link Button} component.
 */
export type ButtonProps = Omit<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >,
  'onClick' | 'button' | 'children'
> &
  DefaultTooltipOptions &
  ExtendedScaleProp & {
    /**
     * The button variant that is used.
     * - 'primary' = orange button
     * - 'gray' = regular gray button
     * - 'tertiary' = button with background of the same color as its parent but gray outline
     * - 'primary-neutral' = button with neutral colors as background and text
     */
    variant: ButtonVariants;

    /**
     * The click handler of the button.
     */
    onClick?: VoidCallback;

    /**
     * The type of the button.
     *
     * `submit` buttons are pressed by the browser when pressing enter in any input inside a surrounding form.
     */
    type?: 'button' | 'submit' | 'reset';

    text: string;

    icon?: ValidIconName;

    showIconAndText?: boolean;

    fontBoostWidthMultiplier?: number;

    fontBoostHeightMultiplier?: number;
  };

/**
 * A simple Button component.
 *
 * @param props a {@link ButtonProps} object.
 * These are simply the properties of a regular <code><button></code> tag
 * with an additional mandatory 'variant' flag to determine what kind of button variant is used.
 * @return the created {@link React.JSX.Element}
 * @constructor
 */
export function Button({
  variant,
  // className property that was passed
  className,
  onClick,
  type = 'button',
  text,
  icon,
  tooltipOptions,
  scale = 'default',
  showIconAndText = false,
  fontBoostWidthMultiplier,
  fontBoostHeightMultiplier,
  ref,
  // the remaining properties that will be passed through to the primitive button
  ...buttonProps
}: Readonly<ButtonProps> & {
  ref?: Ref<HTMLButtonElement>;
}): React.JSX.Element {
  // add button css class depending on variant & scale
  const cName = `ehs-button button-${variant} button-${scale}
  ${className ?? ''} 
  ${(icon && 'icon-button') || ''}
  ${(showIconAndText && 'show-icon-and-text') || ''}`;

  const [isOpen, hoverProps] = useHover(DEFAULT_HOVER_CONFIG);
  const { layerProps, triggerProps, renderLayer } = useLayerDeferedVisibility({
    isOpen,
    ...DEFAULT_PLACEMENT_CONFIG,
    ...tooltipOptions
  });

  // create click handler which doesn't propagate events and which hides the tooltip automatically
  const onClickHidingTooltip = useCallback(() => {
    hoverProps.onMouseLeave();
    onClick?.();
  }, [onClick, hoverProps]);

  const onClickHandler = noPropagationClickHandler(onClickHidingTooltip);

  // return rendered button
  if (icon && showIconAndText == false) {
    return (
      <>
        {isOpen &&
          renderLayer(
            <div {...layerProps} className="ehs-tooltip">
              {text}
            </div>
          )}
        <button
          {...buttonProps}
          {...triggerProps}
          ref={mergeRefs([triggerProps.ref, ref])}
          onMouseEnter={hoverProps.onMouseEnter}
          onMouseLeave={hoverProps.onMouseLeave}
          type={type}
          onClick={onClickHandler}
          className={cName.trim()}
          aria-label={text}
        >
          <ButtonIcon
            iconName={icon}
            fontBoostWidthMultiplier={fontBoostWidthMultiplier}
            fontBoostHeightMultiplier={fontBoostHeightMultiplier}
          />
        </button>
      </>
    );
  } else {
    return (
      <button
        {...buttonProps}
        ref={ref}
        type={type}
        onClick={onClickHandler}
        className={cName.trim()}
        aria-label={undefined}
      >
        {icon && showIconAndText && (
          <ButtonIcon
            iconName={icon}
            fontBoostWidthMultiplier={fontBoostWidthMultiplier}
            fontBoostHeightMultiplier={fontBoostHeightMultiplier}
          />
        )}
        {buttonProps['aria-label'] ? (
          <>
            <span className="sr-only">{buttonProps['aria-label']}</span>
            <span aria-hidden="true">{text}</span>
          </>
        ) : (
          text
        )}
      </button>
    );
  }
}
